import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { BellRing, Plus, X, Loader2 } from "lucide-react";
import DashboardShell from "@/layouts/ProviderDashboardLayout";
import api from "@/lib/api";
import DealCard, { DEAL_STYLES } from "@/components/deals/DealCard";
import { SR_STYLES } from "@/components/servicerequest/QuestionField";
import { useDocumentTitle } from "@/lib/useDocumentTitle";

const catLabel = (c, all) => {
  const p = c.parent_id && all.find((x) => x.id === c.parent_id);
  return p ? `${p.name} › ${c.name}` : c.name;
};

export default function DealAlertsPage() {
  useDocumentTitle("My deal alerts");
  const [data, setData] = useState(null);
  const [cats, setCats] = useState([]);
  const [pick, setPick] = useState("");
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/deals/follows").then((r) => setData(r.data));
  useEffect(() => {
    load();
    api.get("/categories").then((r) => setCats((r.data || []).filter((c) => !/^test/i.test(c.slug || ""))));
  }, []);
  const followed = new Set((data?.follows || []).map((f) => f.category_id));
  const options = cats.filter((c) => !followed.has(c.id)).sort((a, b) => catLabel(a, cats).localeCompare(catLabel(b, cats)));

  const follow = async () => {
    if (!pick) return;
    setBusy(true);
    try { await api.post("/deals/follows", { category_id: pick }); toast.success("You'll get an alert when new deals are posted here"); setPick(""); await load(); }
    catch (e) { toast.error(e?.response?.data?.error || "Could not follow"); } finally { setBusy(false); }
  };
  const unfollow = async (cid) => {
    try { await api.delete(`/deals/follows/${cid}`); toast.success("Stopped following"); load(); }
    catch (e) { toast.error(e?.response?.data?.error || "Could not unfollow"); }
  };

  return (
    <DashboardShell>
      <style>{SR_STYLES + DEAL_STYLES}</style>
      <div className="max-w-5xl" data-testid="deal-alerts-page">
        <div className="text-xs font-bold tracking-[0.2em] uppercase text-[#165a71]">Deal alerts</div>
        <h1 className="mt-1 font-display text-2xl md:text-3xl font-semibold text-[#071b22]">My deal alerts</h1>
        <p className="text-sm text-[#252A34]/65 mt-1">Follow categories and we'll send you an in-app alert the moment a pro posts a new deal there. Following a category also covers its subcategories.</p>

        <div className="mt-6 rounded-3xl border border-slate-200 bg-white p-5 flex flex-col sm:flex-row gap-3" data-testid="deal-alerts-add">
          <select value={pick} onChange={(e) => setPick(e.target.value)} className="sr-input flex-1" data-testid="deal-alerts-category-select">
            <option value="">Choose a category to follow…</option>
            {options.map((c) => <option key={c.id} value={c.id}>{catLabel(c, cats)}</option>)}
          </select>
          <button onClick={follow} disabled={!pick || busy} className="rounded-full bg-[#165a71] text-white px-5 py-2 text-sm font-medium inline-flex items-center justify-center gap-2 disabled:opacity-50 hover:bg-[#11485a] transition-colors" data-testid="deal-alerts-follow-btn">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Follow
          </button>
        </div>

        {data === null ? <div className="mt-6 text-[#252A34]/60">Loading…</div> : (
          <>
            <div className="mt-6 flex flex-wrap gap-2" data-testid="deal-alerts-follows">
              {data.follows.length === 0 && <div className="text-sm text-[#252A34]/60" data-testid="deal-alerts-empty">You're not following any categories yet.</div>}
              {data.follows.map((f) => (
                <span key={f.category_id} className="inline-flex items-center gap-2 rounded-full bg-[#165a71]/10 text-[#165a71] pl-4 pr-2 py-1.5 text-sm" data-testid={`deal-follow-${f.category_id}`}>
                  <BellRing className="h-3.5 w-3.5" />{catLabel(f.category, cats)}
                  <button onClick={() => unfollow(f.category_id)} className="h-6 w-6 rounded-full hover:bg-[#165a71]/15 inline-flex items-center justify-center" aria-label="Unfollow" data-testid={`deal-unfollow-${f.category_id}`}><X className="h-3.5 w-3.5" /></button>
                </span>
              ))}
            </div>
            {data.follows.length > 0 && (
              <section className="mt-10">
                <h2 className="font-display text-xl font-semibold text-[#071b22] mb-4">Live deals in your categories</h2>
                {data.deals.length === 0 ? <div className="text-sm text-[#252A34]/60" data-testid="deal-alerts-no-deals">No live deals right now — we'll alert you when one is posted.</div> : (
                  <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3" data-testid="deal-alerts-deals">
                    {data.deals.map((d, i) => <DealCard key={d.id} d={d} i={i} />)}
                  </div>
                )}
              </section>
            )}
          </>
        )}
      </div>
    </DashboardShell>
  );
}
